/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/cancelEmails.scss';

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ActivateHandle} from "../../../util/interfaces";
import {cancelAllEmails, getPlannedEmailsCount} from "../../../util/requestSender";
import {AppContext} from "../../misc/appContext";
import {
    Checkbox, CloseButton, Loader, ResponseData, showDialog, showSnackbar, WithBackground
} from "@dvrd/dvr-controls";
import {createErrorMessage} from "../../../util/utils";
import {BolmateButton} from "../../controls";

function CancelEmails(props: {}, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [count, setCount] = useState(-1);

    function onActivate() {
        if (!account) return;
        setActive(true);
        getPlannedEmailsCount({
            account_id: account.id, callback: (data: ResponseData) => {
                if (data.success) setCount(data.count);
            }
        });
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setConfirmed(false);
    }

    function onConfirm() {
        setConfirmed(true);
    }

    function onCancel() {
        if (!account) return;
        setLoading(true);
        cancelAllEmails({
            account_id: account.id, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    onClose();
                    showSnackbar('Alle ingeplande e-mails zijn geannuleerd');
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het annuleren van de e-mails is niet gelukt.'),
                        'Annuleren mislukt');
            }
        })
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick={loading} escapeDisabled={loading}>
            <div className='default-popup cancel-emails'>
                <div className='header-container'>
                    <label className='header-title'>E-mails annuleren</label>
                    <CloseButton onClick={onClose} disabled={loading}/>
                </div>
                <div className='body-container'>
                    <Loader active={loading}/>
                    <p className='body-message'>Door de e-mails te annuleren worden <b>alle{count <= 0 ? '' :
                        ` (${count})`}</b> ingeplande e-mails geannuleerd, zodat deze niet meer verzonden worden.
                        Bijvoorbeeld wanneer de ingeplande e-mails een fout bevatten. Deze actie kan niet teruggedraaid
                        worden en geannuleerde e-mails kunnen niet opnieuw ingepland worden.
                        <br/>Afhankelijk van het aantal ingeplande mails kan het annuleren een tijdje duren, <b><u>houd
                            de Emails pagina open</u></b> om zeker te zijn dat alle e-mails geannuleerd worden.
                    </p>
                    <Checkbox checked={confirmed} onCheck={onConfirm}
                              label='Ik bevestig dat alle e-mails geannuleerd moeten worden en begrijp dat dit
                              niet teruggedraaid kan worden'/>
                </div>
                <div className='footer-container btn-2'>
                    <BolmateButton onClick={onClose} label='Sluiten' disabled={loading}/>
                    <BolmateButton onClick={onCancel} label='E-mails annuleren' disabled={loading || !confirmed}
                                   secondary/>
                </div>
            </div>
        </WithBackground>
    )
}

export default forwardRef<ActivateHandle, {}>(CancelEmails);